import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { collection, query, where, orderBy } from "firebase/firestore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { db } from "../../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../../hooks/useFirestoreCollectionOnce";
import { FormPageLayout } from "../../../components/forms/FormPageLayout";
import { FormSection } from "../../../components/forms/FormSection";
import {
  SchemaField,
  type FieldConfig,
} from "../../../components/forms/SchemaField";
import {
  useCreateAudience,
  useUpdateAudience,
} from "../../../hooks/useAudienceMutations";
import { AudiencePicker } from "../../../components/AudiencePicker";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard";
import {
  audienceFormSchema,
  type AudienceFormData,
} from "../../../schemas/audienceFormSchema";
import {
  personalAudienceId,
  type Audience,
  type Role,
  type Session,
} from "@event-planner/shared";

const ROLE_AUDIENCE_SLUG_BY_ROLE: Record<Role, string> = {
  admin: "all-admins",
  organizer: "all-organizers",
  internal: "all-internal",
  invitee: "all-invitees",
  companion: "all-companions",
};

const META_FIELDS: FieldConfig<AudienceFormData>[] = [
  { name: "name", label: "Group name", type: "text", required: true },
  {
    name: "description",
    label: "Description",
    type: "textarea",
    rows: 2,
    helperText: "Internal note for organizers, not shown to attendees.",
  },
];

interface GroupFormProps {
  mode: "create" | "edit";
  initialData?: Audience;
}

/**
 * Custom-group create / edit form (ADR-120, ADR-136). The Groups page
 * filters to named non-system audiences (`name != null && system ==
 * false`), so this form only ever authors that face of the Audience
 * primitive. The `members` spec composes role-based inclusion plus
 * person-keyed direct adds via personal audience IDs; the
 * `onAudienceWritten` trigger resolves into `resolvedPersonIds` which
 * cascade into every consumer's `targetedPersonIds[]` (ADR-137).
 *
 * `system: true` audiences (role audiences and personal audiences) are
 * never edited here, the picker doesn't expose them and the callable
 * rejects mutations on them. The banner + disabled state below is a
 * defense-in-depth render path in case one is reached directly.
 *
 * The legacy contact-staging arm (`stagedAudienceIds[]` +
 * `syncContactStagedAudience`) is gone under ADR-136: pending people are
 * real persons with their own personal audience IDs, so the picker
 * surfaces them uniformly with claimed members.
 */
export function GroupForm({ mode, initialData }: GroupFormProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateAudience();
  const { update, loading: updating } = useUpdateAudience();
  const [serverError, setServerError] = useState<string | null>(null);

  const isSystem = initialData?.system === true;

  const initialMemberAudienceIds = useMemo(() => {
    if (!eventId || !initialData) return [];
    const roleAudienceIds = initialData.members.roles.map(
      (role) => `${eventId}_${ROLE_AUDIENCE_SLUG_BY_ROLE[role]}`,
    );
    const personalAudienceIds = initialData.members.personIds.map(
      (personId: string) => personalAudienceId(eventId, personId),
    );
    return Array.from(
      new Set([
        ...initialData.members.includedAudienceIds,
        ...roleAudienceIds,
        ...personalAudienceIds,
      ]),
    );
  }, [eventId, initialData]);

  const form = useForm<AudienceFormData>({
    resolver: zodResolver(audienceFormSchema),
    defaultValues: initialData
      ? {
          name: initialData.name ?? "",
          description: initialData.description,
          members: {
            roles: [],
            personIds: [],
            includedAudienceIds: initialMemberAudienceIds,
          },
        }
      : {
          name: "",
          description: null,
          members: {
            roles: [],
            personIds: [],
            includedAudienceIds: [],
          },
        },
  });

  // Read-only "Sessions targeting this group" panel, only populated in
  // edit mode. Skip in create mode (no audience ID exists yet) to avoid
  // a meaningless empty list. Surfaces sessions whose `audienceId`
  // points directly at this audience; sessions whose audience is an
  // anonymous composition that includes this group are NOT surfaced
  // here. That edge case is rare and would require either a second
  // query or a server-side roll-up; deliberate v1 omission.
  const targetingSessionsQuery = useMemo(
    () =>
      eventId && initialData?.id
        ? query(
            collection(db, "sessions"),
            where("eventId", "==", eventId),
            where("audienceId", "==", initialData.id),
            orderBy("date", "asc"),
          )
        : null,
    [eventId, initialData?.id],
  );
  const { data: targetingSessions } =
    useFirestoreCollectionOnce<Session>(targetingSessionsQuery);

  const submitting = creating || updating;

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || submitting,
  );

  async function onSubmit(data: AudienceFormData) {
    if (isSystem) return;
    setServerError(null);
    const includedAudienceIds = Array.from(
      new Set(data.members.includedAudienceIds),
    );

    try {
      if (mode === "create") {
        await create({
          eventId: eventId!,
          name: data.name,
          description: data.description ?? null,
          members: {
            roles: [],
            personIds: [],
            includedAudienceIds,
          },
        });
        showSuccess("Group created");
      } else {
        await update({
          audienceId: initialData!.id,
          name: data.name,
          description: data.description ?? null,
          members: {
            roles: [],
            personIds: [],
            includedAudienceIds,
          },
        });
        showSuccess("Group updated");
      }
      navigate(`/events/${eventId}/groups`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save group";
      setServerError(message);
      showError(message);
    }
  }

  return (
    <>
      {unsavedGuardDialog}
      <FormPageLayout
        title={mode === "create" ? "New Group" : "Edit Group"}
        onSubmit={form.handleSubmit(onSubmit)}
        error={serverError}
        submitting={submitting}
        submitLabel={mode === "create" ? "Create Group" : "Save Changes"}
        testIdSlug="group"
        hideBottomActions={isSystem}
      >
        {isSystem && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="system-group-banner">
            This is a system group. Membership is computed automatically from
            event role and the bootstrap definitions, so name, description,
            and members can&apos;t be edited here.
          </Alert>
        )}

        <FormSection title="Group">
          {META_FIELDS.map((field) => (
            <SchemaField
              key={field.name}
              config={{ ...field, disabled: isSystem }}
              control={form.control}
            />
          ))}
        </FormSection>

        <FormSection title="Members">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Build this group from existing groups and people on the event
            roster.
          </Typography>
          <Controller
            control={form.control}
            name="members.includedAudienceIds"
            render={({ field }) => (
              <AudiencePicker
                eventId={eventId}
                value={field.value ?? []}
                onChange={field.onChange}
                label="Group members"
                placeholder="Add groups or people…"
                excludedIds={initialData?.id ? [initialData.id] : []}
                includePendingContacts
                disabled={isSystem}
                testId="group-members"
                required
              />
            )}
          />
        </FormSection>

        {mode === "edit" && initialData && (
          <FormSection title="Sessions targeting this group">
            {targetingSessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sessions reference this group yet. Add it to a session&apos;s
                audience from the session detail page.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {targetingSessions.map((s) => (
                  <Typography
                    key={s.id}
                    variant="body2"
                    data-testid={`group-targeting-session-${s.id}`}
                  >
                    {s.title}
                  </Typography>
                ))}
              </Box>
            )}
          </FormSection>
        )}
      </FormPageLayout>
    </>
  );
}
